import { Button } from "@scribe/ui/button";
import { Card, CardContent } from "@scribe/ui/card";
import BrandLogo from "@/components/brand-logo";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Cpu, FileText, Lock, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/consent/info")({
  component: ConsentInfoPage,
});

function ConsentInfoPage() {
  const handleClose = () => {
    window.close();
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4 py-12">
      <Card className="w-full max-w-2xl overflow-hidden">
        {/* Header section matching the consent form design */}
        <div className="bg-muted/50 flex items-center justify-between border-b px-8 py-5">
          <div className="flex items-center gap-2.5">
            <BrandLogo className="size-6" />
            <span className="text-foreground text-md font-bold tracking-tight">
              Solu Health
            </span>
          </div>
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Data & AI Privacy
          </span>
        </div>

        <CardContent className="space-y-8 p-8">
          <div className="space-y-3">
            <h1 className="text-foreground text-3xl font-extrabold tracking-tight">
              Clinical Audio & AI Disclosure
            </h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Solu Health utilizes secure audio transcription and artificial
              intelligence (AI) to draft clinical documentation. This disclosure
              explains how we process your information, keep it secure, and
              protect your privacy.
            </p>
          </div>

          <div className="space-y-6">
            {/* Section 1: Audio Transcription */}
            <div className="flex gap-4">
              <div className="bg-muted text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
                <FileText className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-foreground text-base font-bold">
                  1. Audio Recording & Transcription
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  During your session, your therapist may audio-record the
                  consultation. This audio is processed securely to create a
                  written transcript. All audio is immediately deleted after
                  transcription. Having a transcription allows your therapist to
                  focus fully on you during your consultation instead of writing
                  notes manually. The transcripts are handled only via secure
                  endpoints.
                </p>
              </div>
            </div>

            {/* Section 2: AI note generation */}
            <div className="flex gap-4">
              <div className="bg-muted text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
                <Cpu className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-foreground text-base font-bold">
                  2. AI-Assisted Summarization
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  The written transcript is analyzed by a secure AI model to
                  draft structured clinical notes (such as summaries, diagnoses,
                  and care plans). This draft is presented back to your
                  therapist. Your therapist reviews, edits, and approves all
                  documentation; the AI does not make decisions regarding your
                  care.
                </p>
              </div>
            </div>

            {/* Section 3: Model Training Exclusion */}
            <div className="flex gap-4">
              <div className="bg-muted text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-foreground text-base font-bold">
                  3. Strict Model Training Protection
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Your data is <strong>never</strong> used to train, retrain, or
                  improve public or private AI models. All API integrations with
                  our AI partners are configured with strict business privacy
                  terms that forbid model training.
                </p>
              </div>
            </div>

            {/* Section 4: Data Security */}
            <div className="flex gap-4">
              <div className="bg-muted text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border">
                <Lock className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-foreground text-base font-bold">
                  4. Encryption & Access Control
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  All clinical transcripts and records are encrypted in transit
                  and at rest. Access is limited strictly to authorized
                  clinicians within your provider's organization. We comply with
                  health records privacy laws and secure cloud infrastructure
                  standards.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 border-border space-y-2.5 rounded-lg border p-5">
            <h3 className="text-foreground text-sm font-bold">
              Your Right to Choose & Withdraw Consent
            </h3>
            <p className="text-muted-foreground text-xs leading-relaxed">
              Consenting to audio recording and AI note-taking is completely
              voluntary. You are free to refuse either option. If you change
              your mind, you can withdraw your consent at any time by notifying
              your healthcare provider, and future sessions will not be recorded
              or summarized.
            </p>
          </div>

          <div className="border-border flex justify-end border-t pt-4">
            <Button onClick={handleClose} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Close Window
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
